import * as moment from "moment";

export namespace ParserHelper {
    export const EPSILON = 0.0000000001;

    export type Primitives = boolean | number | string | typeof Date;

    // #region Type Wrappers

    export const Type = {
        get Integer() {
            return (value: string) => toInt(value);
        },
        get Float() {
            return (value: string) => toFloat(value);
        },
        get Date() {
            return (value: string) => toDate(value);
        },
        get Time() {
            return (value: string) => toTime(value);
        },
        get String() {
            return (value: string) => toString(value);
        },
        Enum<T>(object: T) {
            return (value: string) => toEnumValue(object, value);
        }
    };

    // #endregion

    /**
     * Convert the input string to a boolean. Everything except for '0', 'false' and '' returns true.
     * In strict mode only '1' and 'true' return true.
     * @param str Input string
     * @param strict Flag that indicates strict mode for parser. False, by default.
     */
    export function toBoolean(str: string, strict = false): boolean | undefined {
        // check if already parsed
        if (typeof str === "boolean") {
            return str;
        }
        // return undefined if not a string
        if (typeof str !== "string") {
            return undefined;
        }
        if (strict) {
            if (str === "1" || str === "true" || str === "TRUE") {
                return true;
            } else if (str === "0" || str === "false" || str === "FALSE") {
                return false;
            } else {
                return undefined;
            }
        }
        return str !== "0" && str !== "false" && str !== "FALSE" && str !== "";
    }

    /**
     * Convert the input string to an integer.
     * @param str Input string
     * @param radix Numeral system to be used. 10, by default.
     * @returns parsed integer, null if input string is empty, or NaN if not a number.
     */
    export function toInt(str: string, radix = 10): number | null {
        // check if already parsed
        if (typeof str === "number") {
            return str;
        }
        // return null if not a string
        if (typeof str !== "string") {
            return null;
        }
        if (str === "") {
            return null;
        }
        // parse int
        return parseInt(str, radix);
    }

    export function toFloat(str: string): number | null {
        // check if already parsed
        if (typeof str === "number") {
            return str;
        }
        // return null if not a string
        if (typeof str !== "string") {
            return null;
        }
        if (str === "") {
            return null;
        }
        // parse float with rounding up to epsilon to cover the inaccuracy of type conversion
        return Math.round(parseFloat(str) * EPSILON) / EPSILON;
    }

    /**
     * Convert the input string (date or timestamp) to a date object, or null if the input is not a date.
     */
    export function toDate(str: string): Date | null {
        if (!str) {
            return null;
        }

        // Timestamps should comply with ISO 8601 format and look like "2020-01-07T17:26" or "2020-01-07T17:26Z" (Z for UTC).
        let parsedTimestamp = moment(str, moment.ISO_8601);

        // Make sure timestamp follows ISO 8601 format and ends with Z for UTC -> Local conversion,
        // otherwise local time zone is ignored and server time is rendered for end users.
        if (parsedTimestamp.isValid() && /T/i.test(str) && !/Z$/i.test(str)) {
            parsedTimestamp = moment(str + "Z", moment.ISO_8601);
        }

        // Check if timestamp parsed by moment.js is valid and return plain Date object
        if (parsedTimestamp.isValid()) {
            return parsedTimestamp.toDate();
        }

        // Otherwise delegate parsing to browser API
        const date = Date.parse(str);
        return !isNaN(date) ? new Date(date) : null;
    }

    /**
     * Convert the input string (date or timestamp) to moment duration representing time, or null if the input is not a date.
     */
    export function toTime(str: string): moment.Duration | null {
        if (!str || typeof str !== "string") {
            return null;
        }
        const duration = moment.duration(str);
        return duration.isValid() ? duration : null;
    }

    export function toEnum<T>(object: T) {
      return (str: string) => toEnumValue(object, str);
    }

    /**
     * Convert the input string to enum value using underlying type (string or integer by default).
     */
    export function toEnumValue<T>(object: T, str: string): T[keyof T] {
        const value = (object as any)[str];
        const key = (object as any)[value];
        return key;
    }

    /**
     * Convert anything to string.
     */
    export function toString(value: string): string {
        return String(value || "");
    }

    /**
     * Convert anything to object.
     */
    export function toObject(value: string): any {
        return typeof value === "object" ? value : null;
    }
}
